import cx from 'classnames';
import { useMemo, type VoidFunctionComponent } from 'react';
import type { Editor } from 'codemirror';

import { toPercentage } from '../../../../../../shared/utils';
import { useDresser } from '../../../../../contexts/DrawerContext';
import { useOnMount } from '../../../../../hooks';
import { useSnippetMatchDetails } from '../../../../../hooks/api/snippets';
import { useSnippetRetentionMetadata } from '../../../../../hooks/api/snippets/useSnippetRetentionMetadata';
import { Badge } from '../../../../generic/Badge';
import type { SnippetMatch, SnippetMatchDetails } from '../../../../../../shared/snippets';

import CodeMirror from './CodeMirror';
import FileTree from './FileTree';

import styles from './SnippetsDrawerCode.module.less';

const CODE_VIEW_DRAWER_WIDTH_PERCENT = 72;

/**
 * Small hook which "builds" the file contents for both the userland file as well as the matched snippet.
 */
function useBuildFileContents(matchDetails?: SnippetMatchDetails) {
  const detectedFile = useMemo(() => {
    if (!matchDetails) {
      return '';
    }
    return matchDetails.detectedCode.map(line => line.line).join('\n');
  }, [matchDetails]);
  const referenceFile = useMemo(() => {
    if (!matchDetails) {
      return '';
    }
    return matchDetails.referenceCode.map(line => line.line).join('\n');
  }, [matchDetails]);

  return {
    detectedFile,
    referenceFile,
  };
}

type Props = {
  /**
   * Revision locator for this project.
   */
  userRevisionLocator: string;
  /**
   * The ID of the snippet which is currently selected.
   */
  selectedSnippetId: string;
  /**
   * Matches from the snippet
   */
  matches: SnippetMatch[];
  /**
   * The currently selected path in the code view.
   */
  selectedPath: string | null;
  /**
   * Function to set the selected path in the code view.
   */
  setSelectedPath: (path: string | null) => void;
};

/**
 * Code tab for the SnippetDrawer.
 * Displays code matches for a given snippet.
 */
const SnippetsDrawerCode: VoidFunctionComponent<Props> = ({
  userRevisionLocator,
  selectedSnippetId,
  matches,
  selectedPath,
  setSelectedPath,
}) => {
  // Expand width when code view is active to improve visibility on smaller screens.
  const { setWidthPercent } = useDresser();
  useOnMount(() => setWidthPercent(CODE_VIEW_DRAWER_WIDTH_PERCENT), () => setWidthPercent(undefined));

  //  This makes it much faster to determine the matchPercentage given a path.
  const pathToMatchPercentage: Map<SnippetMatch['path'], `${number}`> = useMemo(() =>
    new Map(matches.map(match => [match.path, toPercentage(match.matchPercentage)])), [matches]);

  //  Handle fetching the match details
  const shouldFetch = !!selectedPath;
  const { data, isLoading } = useSnippetMatchDetails({
    locator: userRevisionLocator,
    snippetId: selectedSnippetId,
    path: selectedPath || '', // We don't attempt to fetch if the path isn't there.
  }, shouldFetch);

  //  The data is returned line-by-line, this hook simply joins them to "build" each file.
  const { detectedFile, referenceFile } = useBuildFileContents(data?.matchDetails);

  const { data: retentionMetadata } = useSnippetRetentionMetadata(userRevisionLocator);

  return (
    <div className={styles.layout}>
      <aside className={styles.files}>
        <header className={styles.header}>
          Files
        </header>
        <div className={styles.fileTreeWrapper}>
          <FileTree
            matches={matches}
            selectedPath={selectedPath}
            setSelectedPath={setSelectedPath}
            selectedSnippetId={selectedSnippetId}
          />
        </div>
      </aside>
      <section className={cx(styles.pane, styles.thirdParty)}>
        <header className={styles.header}>
          3rd Party Code
        </header>
        <CodeMirror
          isLoading={isLoading}
          selectedPath={selectedPath}
          fileContents={referenceFile}
          editorDidMount={(editor: Editor) => {
            let firstHighlightedLineNumber: number | undefined;

            if (data?.matchDetails?.referenceCode) {
              data.matchDetails.referenceCode.forEach((line, index) => {
                if (line.isHighlighted) {
                  if (firstHighlightedLineNumber === undefined) {
                    firstHighlightedLineNumber = index;
                  }
                  editor.addLineClass(index, 'background', styles.highlightedLine);
                }
              });
            }

            if (firstHighlightedLineNumber !== undefined) {
              const top = editor.charCoords({ line: firstHighlightedLineNumber, ch: 0 }, 'local').top;
              editor.scrollTo(null, top);
            }
          }}
        />
      </section>
      <section className={cx(styles.pane, styles.yourCode)}>
        <header className={styles.header}>
          Your Code
          {selectedPath && pathToMatchPercentage.get(selectedPath) && (
            <Badge color="light-gray" size="micro">
              {pathToMatchPercentage.get(selectedPath) || 0}% match
            </Badge>
          )}
        </header>
        <CodeMirror
          isLoading={isLoading}
          selectedPath={selectedPath}
          detectedAt={retentionMetadata?.detectedAt ? new Date(retentionMetadata.detectedAt) : undefined}
          expiresAt={retentionMetadata?.expiresAt ? new Date(retentionMetadata.expiresAt) : undefined}
          fileContents={detectedFile}
          editorDidMount={(editor: Editor) => {
            let firstHighlightedLineNumber: number | undefined;

            if (data?.matchDetails?.detectedCode) {
              data.matchDetails.detectedCode.forEach((line, index) => {
                if (line.isHighlighted) {
                  if (firstHighlightedLineNumber === undefined) {
                    firstHighlightedLineNumber = index;
                  }
                  editor.addLineClass(index, 'background', styles.highlightedLine);
                }
              });
            }

            if (firstHighlightedLineNumber !== undefined) {
              const top = editor.charCoords({ line: firstHighlightedLineNumber, ch: 0 }, 'local').top;
              editor.scrollTo(null, top);
            }
          }}
        />
      </section>
    </div>
  );
};

export default SnippetsDrawerCode;
